import { MouseEventHandler } from "react";
import cx from "classnames";
import { HeartIcon } from "@heroicons/react/solid";
import useFavoriteExpert from "hooks/useFavoriteExpert";
import { HeartIcon as HeartIconOutline } from "@heroicons/react/outline";
import { translate } from "utils";
import { resolveFollowButtonState } from "./followButtonState";
import styles from "./FollowButtonOffer.module.css";

type FollowButtonVariant = "default" | "danger";
type FollowButtonSize = "default" | "largeMobile";

interface FollowButtonOfferProps {
  handleFollow: MouseEventHandler<HTMLButtonElement>;
  handleUnfollow?: MouseEventHandler<HTMLButtonElement>;
  expertId?: string;
  isFollowed: boolean;
  outline?: boolean;
  variant?: FollowButtonVariant;
  iconOnly?: boolean;
  iconOnlyWhenFollowed?: boolean;
  hideWhenFollowed?: boolean;
  size?: FollowButtonSize;
}

const getButtonClassName = ({
  variant,
  size,
  iconOnly,
  showFollowedHeartOnly,
}: {
  variant: FollowButtonVariant;
  size: FollowButtonSize;
  iconOnly: boolean;
  showFollowedHeartOnly: boolean;
}) => {
  if (variant !== "danger") {
    return iconOnly ? styles.defaultIconButton : styles.defaultTextButton;
  }

  if (showFollowedHeartOnly) {
    return styles.followedHeartOnlyButton;
  }

  if (size === "largeMobile") {
    return styles.dangerLargeMobileTextButton;
  }

  return styles.dangerTextButton;
};

const getIconColorClassName = ({
  variant,
  showFollowedHeartOnly,
}: {
  variant: FollowButtonVariant;
  showFollowedHeartOnly: boolean;
}) => {
  if (showFollowedHeartOnly) {
    return styles.followedHeartOnlyIcon;
  }

  if (variant === "danger") {
    return styles.dangerIcon;
  }

  return styles.defaultIcon;
};

const getIconSizeClassName = ({
  variant,
  size,
  showFollowedHeartOnly,
}: {
  variant: FollowButtonVariant;
  size: FollowButtonSize;
  showFollowedHeartOnly: boolean;
}) => {
  if (showFollowedHeartOnly) {
    return styles.defaultIconSize;
  }

  if (variant === "danger" && size === "largeMobile") {
    return styles.largeMobileIconSize;
  }

  return styles.defaultIconSize;
};

const getIconClassName = ({
  variant,
  size,
  showFollowedHeartOnly,
}: {
  variant: FollowButtonVariant;
  size: FollowButtonSize;
  showFollowedHeartOnly: boolean;
}) =>
  cx(
    getIconColorClassName({ variant, showFollowedHeartOnly }),
    getIconSizeClassName({ variant, size, showFollowedHeartOnly })
  );

const FollowButtonOffer = ({
  handleFollow,
  handleUnfollow,
  expertId,
  isFollowed,
  outline,
  variant = "default",
  iconOnly = false,
  iconOnlyWhenFollowed = false,
  hideWhenFollowed = false,
  size = "default",
}: FollowButtonOfferProps) => {
  const isExpertFavorite = useFavoriteExpert(expertId)?.isFavorite;
  const { following, shouldHide, canUnfollow } = resolveFollowButtonState({
    isFollowed,
    isExpertFavorite,
    hideWhenFollowed,
    hasUnfollowHandler: Boolean(handleUnfollow),
  });

  if (shouldHide) {
    return null;
  }

  const Icon =
    following || !outline || variant === "danger"
      ? HeartIcon
      : HeartIconOutline;
  const showFollowedHeartOnly = Boolean(iconOnlyWhenFollowed && following);
  const shouldShowIconOnly = iconOnly || showFollowedHeartOnly;
  const label = translate(following ? "following" : "follow");

  return (
    <button
      type="button"
      onClick={following ? handleUnfollow : handleFollow}
      aria-pressed={following}
      aria-label={shouldShowIconOnly ? label : undefined}
      className={getButtonClassName({
        variant,
        size,
        iconOnly: shouldShowIconOnly,
        showFollowedHeartOnly,
      })}
      disabled={following && !canUnfollow}
    >
      <Icon
        className={getIconClassName({
          variant,
          size,
          showFollowedHeartOnly,
        })}
      />
      {shouldShowIconOnly ? null : <span>{label}</span>}
    </button>
  );
};

export default FollowButtonOffer;
